<template>
  <div v-if="visible" class="history-overlay">
    <div class="history-dialog">
      <div class="history-header">
        <h5 class="history-title">{{ title }}</h5>
        <button type="button" class="close-btn" @click="$emit('close')">&times;</button>
      </div>
      <div class="history-body">
        <div class="timeline">
          <div
            v-for="entry in entries"
            :key="entry.id"
            class="timeline-item"
            :class="{ active: selectedId === entry.id }"
            @click="selectEntry(entry)"
          >
            <settlement-status-badge :status="entry.statusAfter" />
            <div class="event-label">{{ eventLabel(entry.eventType) }}</div>
            <div class="event-meta">{{ formatDateTime(entry.createdAt) }}</div>
            <div class="event-meta">{{ entry.adminName }}</div>
          </div>
          <p v-if="entries.length === 0" class="empty">Brak historii zmian.</p>
        </div>
        <div class="snapshot">
          <p v-if="!selectedSnapshot" class="empty">Wybierz wpis z osi czasu, aby zobaczyć stan tabel.</p>
          <settlement-order-table
            v-for="table in snapshotTables"
            :key="table.orderId"
            :table="table"
            :readonly="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SettlementOrderTable from "./SettlementOrderTable.vue";
import SettlementStatusBadge from "./SettlementStatusBadge.vue";

const MONTHS = ["styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec", "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień"];
const EVENT_LABELS = {
  DRAFT_SAVE: "Zapis zmian",
  SUBMIT: "Przekazano do weryfikacji",
  SETTLE: "Rozliczono",
  RETURN_TO_FIX: "Cofnięto do poprawy",
  POST_SETTLEMENT_EDIT: "Edycja po rozliczeniu",
};

export default {
  name: "SettlementHistoryModal",
  components: { SettlementOrderTable, SettlementStatusBadge },
  props: {
    visible: { type: Boolean, default: false },
    employeeId: { type: String, default: null },
    employeeName: { type: String, default: "" },
    month: { type: Number, default: null },
    year: { type: Number, default: null },
  },
  emits: ["close"],
  data() {
    return {
      entries: [],
      selectedId: null,
      selectedSnapshot: null,
    };
  },
  computed: {
    title() {
      const period = this.month ? `${MONTHS[this.month - 1]} ${this.year}` : "";
      return `Historia zmian rozliczenia opiekuna ${this.employeeName} - ${period}`;
    },
    snapshotTables() {
      return this.selectedSnapshot && this.selectedSnapshot.tables ? this.selectedSnapshot.tables : [];
    },
  },
  watch: {
    visible(val) {
      if (val) this.fetch();
    },
  },
  methods: {
    async fetch() {
      this.selectedId = null;
      this.selectedSnapshot = null;
      try {
        this.entries = await this.$store.dispatch("getSettlementHistory", {
          employeeId: this.employeeId,
          month: this.month,
          year: this.year,
        });
        if (this.entries.length > 0) {
          this.selectEntry(this.entries[0]);
        }
      } catch (e) {
        this.$notify({ group: "error", text: "Nie udało się pobrać historii" });
      }
    },
    async selectEntry(entry) {
      this.selectedId = entry.id;
      try {
        const details = await this.$store.dispatch("getSettlementHistoryEntry", entry.id);
        this.selectedSnapshot = details.snapshot;
      } catch (e) {
        this.$notify({ group: "error", text: "Nie udało się pobrać stanu tabel" });
      }
    },
    eventLabel(eventType) {
      return EVENT_LABELS[eventType] || eventType;
    },
    formatDateTime(value) {
      const d = new Date(value);
      const p = (n) => String(n).padStart(2, "0");
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.history-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 40px 16px;
  overflow-y: auto;
}
.history-dialog {
  background: #fff;
  border-radius: 8px;
  width: 1100px;
  max-width: 96vw;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.history-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}
.history-title {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}
.close-btn {
  position: absolute;
  right: 16px;
  top: 10px;
  border: none;
  background: none;
  font-size: 32px;
  font-weight: 200;
  cursor: pointer;
  line-height: 1;
}
.history-body {
  display: flex;
  gap: 16px;
  padding: 16px 24px;
  max-height: 70vh;
}
.timeline {
  width: 240px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  overflow-y: auto;
  padding-right: 8px;
}
.timeline-item {
  padding: 10px 8px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 6px;
  border: 1px solid transparent;
}
.timeline-item:hover {
  background: #faf5f8;
}
.timeline-item.active {
  border-color: #d6006d;
  background: #faf5f8;
}
.event-label {
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
}
.event-meta {
  font-size: 11px;
  color: #888;
}
.snapshot {
  flex: 1;
  overflow-y: auto;
}
.empty {
  color: #999;
  font-size: 13px;
  padding: 16px;
  text-align: center;
}
</style>
